import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BrandMark } from "./Brand";

export const NAV_LINKS = [
  { to: "/about", label: "About" },
  { to: "/ministries", label: "Ministries" },
  { to: "/outreach", label: "Outreach" },
  { to: "/events", label: "Events" },
  { to: "/watch", label: "Watch" },
  { to: "/give", label: "Give" },
  { to: "/contact", label: "Contact" },
];

const MOBILE_EXTRA = [
  { to: "/prayer", label: "Prayer" },
  { to: "/serve", label: "Serve" },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || pathname !== "/";

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          solid ? "bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#27272A]" : "bg-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between py-4 md:py-5">
          <BrandMark size="md" />

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${slug(l.label)}`}
                className={({ isActive }) =>
                  `text-[11px] font-bold uppercase tracking-[0.22em] transition-colors relative py-1 ${
                    isActive
                      ? "text-white after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-[2px] after:bg-[var(--brand-red)]"
                      : "text-white/70 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/new-here" data-testid="nav-cta-plan-visit" className="hidden md:inline-flex btn-primary-sharp">
              Plan Your Visit
            </Link>
            <button
              type="button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-[#27272A] text-white"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <div
        data-testid="mobile-nav"
        aria-hidden={!open}
        className={`fixed inset-0 z-40 bg-[#0A0A0A] lg:hidden transition-transform duration-500 overflow-y-auto ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="container-x pt-28 pb-16 flex flex-col gap-5">
          <NavLink to="/" end data-testid="mobile-nav-link-home" className="font-display font-black uppercase text-4xl tracking-tight text-white/90 hover:brand-red transition-colors">
            Home
          </NavLink>
          {[...NAV_LINKS, ...MOBILE_EXTRA].map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-link-${slug(l.label)}`}
              className={({ isActive }) =>
                `font-display font-black uppercase text-4xl tracking-tight transition-colors ${
                  isActive ? "brand-red" : "text-white/90 hover:brand-red"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/new-here" data-testid="mobile-nav-cta" className="btn-primary-sharp mt-6 self-start">
            Plan Your Visit
          </Link>
        </div>
      </div>
    </>
  );
}
